import {
  type DatabaseConnectionNavSubItem,
  type DatabaseSchemaNavSubItem,
  databaseGeneralSettingsPath,
  databaseUsersPath,
} from './databaseContextNav'
import { databaseConnectionPath, isDatabaseConnectionPath } from './databaseConnectionNav'
import { databaseSchemaPath, isDatabaseSchemaPath } from './databaseSchemaNav'

export type SyntheticDatabaseNavItemKind =
  | 'seed_plan'
  | 'general'
  | 'preview_queries'
  | 'snapshots'
  | 'forks'
  | 'users'
  | 'schema'
  | 'connection'

export type SyntheticDatabaseNavItem = {
  kind: SyntheticDatabaseNavItemKind
  label: string
  path: string
}

export function syntheticDatabaseSeedPlanPath(databaseId: string | number): string {
  return `/synthetic-databases/${databaseId}/seed-plan`
}

/** Flat items around Schema / Connection expandable groups. */
export function getSyntheticDatabaseNavItems(databaseId: string): SyntheticDatabaseNavItem[] {
  return [
    {
      kind: 'seed_plan',
      label: 'Seed plan',
      path: syntheticDatabaseSeedPlanPath(databaseId),
    },
    {
      kind: 'general',
      label: 'General',
      path: databaseGeneralSettingsPath(databaseId),
    },
    {
      kind: 'preview_queries',
      label: 'Preview queries',
      path: `/databases/${databaseId}/preview-queries`,
    },
    {
      kind: 'snapshots',
      label: 'Snapshots',
      path: `/databases/${databaseId}/snapshots`,
    },
    {
      kind: 'forks',
      label: 'Forks',
      path: `/databases/${databaseId}/forks`,
    },
    {
      kind: 'users',
      label: 'Users',
      path: databaseUsersPath(databaseId),
    },
  ]
}

export function getSyntheticDatabaseSchemaNavSubItems(
  databaseId: string,
): DatabaseSchemaNavSubItem[] {
  return [
    { view: 'diagram', label: 'Diagram', path: databaseSchemaPath(databaseId, 'diagram') },
    { view: 'table', label: 'Table', path: databaseSchemaPath(databaseId, 'table') },
  ]
}

export function getSyntheticDatabaseConnectionNavSubItems(
  databaseId: string,
): DatabaseConnectionNavSubItem[] {
  return [
    { view: 'downstream', label: 'Downstream', path: databaseConnectionPath(databaseId, 'downstream') },
    { view: 'parameters', label: 'Parameters', path: databaseConnectionPath(databaseId, 'parameters') },
  ]
}

export function syntheticDatabaseSchemaNavItemPath(databaseId: string): string {
  return databaseSchemaPath(databaseId, 'diagram')
}

export function syntheticDatabaseConnectionNavItemPath(databaseId: string): string {
  return databaseConnectionPath(databaseId, 'downstream')
}

export function isSyntheticDatabaseNavItemActive(
  pathname: string,
  databaseId: string,
  item: SyntheticDatabaseNavItem,
): boolean {
  const normalizedPath = pathname.replace(/\/$/, '')
  const normalizedItemPath = item.path.replace(/\/$/, '')
  if (item.kind === 'seed_plan') {
    return (
      normalizedPath === `/synthetic-databases/${databaseId}` ||
      normalizedPath === `/synthetic-databases/${databaseId}/seed-plan`
    )
  }
  return normalizedPath === normalizedItemPath || pathname.startsWith(`${item.path}/`)
}

export function isOnSyntheticDatabaseSchema(pathname: string, databaseId: string): boolean {
  return isDatabaseSchemaPath(pathname, databaseId)
}

export function isOnSyntheticDatabaseConnection(pathname: string, databaseId: string): boolean {
  return isDatabaseConnectionPath(pathname, databaseId)
}
